/**
 * Alfa-Field Connect — App Launcher Modules
 * ═══════════════════════════════════════════════════════════════════
 * Configuración central de todos los módulos disponibles.
 * Se muestran en el App Launcher (home) tipo Odoo y en el ModuleTopBar
 * (dropdown de sub-secciones cuando estás dentro de un módulo).
 *
 * Cada módulo declara:
 *  - id: identificador único
 *  - name: nombre visible
 *  - icon: componente lucide-react
 *  - to: ruta principal
 *  - color: paleta ("blue" | "orange" | "purple" | "teal" | "amber"...)
 *  - group: categoría lógica (Operación / Administración / Documentación)
 *  - perm / roles: control de acceso (opcional)
 *  - submenu: sub-secciones que se muestran en el dropdown superior
 */
import {
  ShieldCheck,   // v101.255.0
  Receipt,   // v101.193.0
  Timer,
  ClipboardList,
  Boxes,
  Warehouse,
  Workflow,
  Users,
  Shield,
  Building2,
  History,
  Mail,
  Database,
  HardDrive,
  Brain,
  SlidersHorizontal,
  Cable,
  Map,
  MapPin,
  CalendarClock,
  AlertOctagon,
  Target,
  Route,
  HeartPulse,
  Bot,
  FileText,
  BookOpen,
  Bell,
  Radar,
  Zap,
  Layers,
  Package,
  AlertTriangle,
  ListOrdered,
  MapPinned,
  MessageSquare,
  Activity,
  CalendarDays,
} from "lucide-react";

// Paleta corporativa — cada módulo tiene un color de acento suave
// (fondo icono) + color sólido (icono). Estilo Odoo moderno.
export const COLOR_PALETTE = {
  blue:    { bg: "#DBEAFE", solid: "#2563EB", ring: "#93C5FD" },
  cyan:    { bg: "#CFFAFE", solid: "#0891B2", ring: "#67E8F9" },
  teal:    { bg: "#CCFBF1", solid: "#0D9488", ring: "#5EEAD4" },
  green:   { bg: "#DCFCE7", solid: "#16A34A", ring: "#86EFAC" },
  lime:    { bg: "#ECFCCB", solid: "#65A30D", ring: "#BEF264" },
  amber:   { bg: "#FEF3C7", solid: "#D97706", ring: "#FCD34D" },
  orange:  { bg: "#FFEDD5", solid: "#EA580C", ring: "#FDBA74" },
  red:     { bg: "#FEE2E2", solid: "#DC2626", ring: "#FCA5A5" },
  rose:    { bg: "#FFE4E6", solid: "#E11D48", ring: "#FDA4AF" },
  pink:    { bg: "#FCE7F3", solid: "#DB2777", ring: "#F9A8D4" },
  fuchsia: { bg: "#FAE8FF", solid: "#C026D3", ring: "#F0ABFC" },
  purple:  { bg: "#EDE9FE", solid: "#7C3AED", ring: "#C4B5FD" },
  indigo:  { bg: "#E0E7FF", solid: "#4F46E5", ring: "#A5B4FC" },
  slate:   { bg: "#E2E8F0", solid: "#475569", ring: "#94A3B8" },
};

/**
 * Módulos productivos. El orden aquí es el que se muestra en el launcher.
 */
export const APP_MODULES = [
  // ══════════════ OPERACIÓN ══════════════
  {
    id: "ordenes",
    name: "Órdenes de Trabajo",
    tagline: "Inbox Smart · Kanban · Calendario · Mapa",
    icon: ClipboardList,
    to: "/ordenes",
    color: "blue",
    group: "Gestión de Operación",
    perm: "M1.view",
    submenu: [
      { label: "Bandeja OT Smart", to: "/ordenes" },
      { label: "War Room Unificado", to: "/warroom" },
      { label: "Calendario Técnicos", to: "/calendario-tecnicos" },
      { label: "Nueva Orden", to: "/ordenes/nueva", perm: "M1.create" },
    ],
  },
  {
    // v3.9.1 · Centro Maestro de Fiscalización (módulo de primer nivel)
    id: "fiscalizacion-center",
    name: "📋 Centro de Fiscalización",
    tagline: "Reglas IA · Constructor de Formularios · Fotos de referencia",
    icon: ClipboardList,
    to: "/fiscalizacion",
    color: "red",
    group: "Gestión de Operación",
    roles: ["admin", "super_admin", "admin_zone", "fiscalizer"],
  },
  {
    // v3.5.0 · War Room Unificado (reemplaza CalendarioTecnicos + MapaOTsDia)
    id: "warroom",
    name: "War Room Unificado",
    tagline: "Mapa táctico · Smart Pins",
    icon: MapPin,
    to: "/warroom",
    color: "cyan",
    group: "Gestión de Operación",
    perm: "M1.view",
  },
  // v10 · CONSOLIDADO en submenu de "Órdenes de Trabajo"
  {
    // ★ Inventario Maestro (unificado desde Odoo · reemplaza el módulo legacy)
    id: "inventario-master",
    name: "Inventario Maestro",
    tagline: "Stock Odoo en vivo",
    icon: Layers,
    to: "/inventario-master",
    color: "amber",
    group: "Gestión de Operación",
    perm: "M6.inventory.view",
  },
  {
    // v101.120.0 · Kardex por bodega con trazabilidad a la OT
    id: "kardex-inventario",
    name: "Kardex de Inventario",
    tagline: "Ingresos y egresos por bodega con la OT",
    icon: Layers,
    to: "/kardex",
    color: "amber",
    group: "Gestión de Operación",
    perm: "M6.inventory.view",
  },
  {
    // M2 · Categorías de materiales con página propia (QA 23-07)
    id: "categorias-materiales",
    name: "Categorías Material",
    tagline: "Catálogo global",
    icon: Warehouse,
    to: "/categorias-materiales",
    color: "amber",
    group: "Gestión de Operación",
    perm: "M6.inventory.view",
  },
  {
    id: "tipos-solucion",
    name: "Tipos de Solución",
    tagline: "Taxonomía corporativa",
    icon: Workflow,
    to: "/tipos-solucion",
    color: "teal",
    group: "Gestión de Operación",
    perm: "M7.solution_types.view",
  },
  {
    // v1.9.9 · Odoo Explorer (integración solo lectura)
    id: "odoo-explorer",
    name: "Odoo Explorer",
    tagline: "8 tablas Odoo en vivo",
    icon: Database,
    to: "/odoo",
    color: "purple",
    group: "Integración",
    perm: "M25.monitoring.view",
  },
  // v10 · ELIMINADO: OT SMART (duplicado, consolidado en "Órdenes de Trabajo")
  // v10 · ELIMINADO: War Room legacy (consolidado en "War Room Unificado")
  {
    // v2.9.8 · Flow Monitor · trazabilidad end-to-end de una OT
    id: "flow-monitor",
    name: "Flow Monitor",
    tagline: "Trazabilidad OT · Odoo → A1 → A2 → Kanban",
    icon: Route,
    to: "/gestion/flow-monitor",
    color: "cyan",
    group: "Centro de Inteligencia",
    perm: "M25.monitoring.view",
  },

  // ══════════════ ADMINISTRACIÓN ══════════════
  // ══════════════ CENTRO DE INTELIGENCIA ══════════════
  // v10 · Renombrado "Cerebro" → "Centro de Inteligencia"
  // Consolidado: A3 (Eventos+Calamidades), WhatsApp (Negociaciones+Plantillas+Notif)
  // Eliminados: Constructor Formularios, Dry-Run A2, Mapeo Stages, Imprevistos
  {
    id: "reglas-agentes",
    name: "Configuración de Reglas",
    tagline: "A1 · A2 · A3 · A4 · A5 · A6",
    icon: Brain,
    to: "/admin/reglas-agentes",
    color: "purple",
    group: "Centro de Inteligencia",
    roles: ["admin", "super_admin", "admin_zone", "supervisor"],
  },
  // v10 · ELIMINADO del launcher: Constructor de Formularios (consolidado en Fiscalización)
  // Ruta sigue activa: /admin/formularios-fiscalizacion
  // v10 · ELIMINADO del launcher: Dry-Run A2 (consolidado en Agente A2)
  // Ruta sigue activa: /admin/a2-dryrun
  // ══════════════ CARDS DEDICADAS A1-A6 (v2.8.8.8) ══════════════
  // Visibles automáticamente cuando el usuario tiene el Nivel 1 (view) del
  // agente correspondiente. Ordenadas A1→A6 dentro del grupo Cerebro.
  {
    id: "agent-a1",
    name: "Agente A1 · Predictor",
    tagline: "Materiales · alertas de stock",
    icon: Package,
    to: "/admin/a1-audit",
    color: "amber",
    group: "Centro de Inteligencia",
    perm: "M31.a1.view",
  },
  // v10 · ELIMINADO del launcher: Agente A2 (duplicado con Dry-Run, consolidado)
  {
    id: "agent-a3",
    name: "Agente A3 · Eventos y Calamidades",
    tagline: "Bloqueos por zona · Eventos masivos",
    icon: AlertTriangle,
    to: "/admin/eventos-masivos",
    color: "red",
    group: "Centro de Inteligencia",
    perm: "M33.a3.view",
    submenu: [
      { label: "Eventos Masivos", to: "/admin/eventos-masivos" },
      { label: "Calamidades", to: "/admin/calamidades-a5" },
    ],
  },
  {
    id: "agent-a4",
    name: "Agente A4 · Ruteo",
    tagline: "Rutas optimizadas del día",
    icon: Route,
    to: "/admin/rutas-a4",
    color: "teal",
    group: "Centro de Inteligencia",
    perm: "M34.a4.view",
  },
  {
    id: "gestion-whatsapp",
    name: "Gestión WhatsApp",
    tagline: "Negociaciones · Notificaciones · Plantillas",
    icon: MessageSquare,
    to: "/admin/a2-negotiations",
    color: "green",
    group: "Centro de Inteligencia",
    perm: "M35.a5.view",
    roles: ["admin", "super_admin", "admin_zone", "supervisor"],
    submenu: [
      { label: "Conversaciones (Fibi)", to: "/whatsapp/conversaciones" },   // v101.196.0
      { label: "Negociaciones", to: "/admin/a2-negotiations" },
      { label: "Plantillas WhatsApp", to: "/admin/whatsapp-templates" },
      { label: "Conexión WhatsApp (Meta)", to: "/admin/whatsapp-conexion", roles: ["admin", "super_admin"] },
    ],
  },
  {
    id: "agent-a6",
    name: "Agente A6 · Cola Priorizada",
    tagline: "Ranking dinámico de OTs",
    icon: ListOrdered,
    to: "/admin/cola-a6",
    color: "indigo",
    group: "Centro de Inteligencia",
    perm: "M36.a6.view",
  },
  {
    id: "reglas-zona",
    name: "Reglas por Zona",
    tagline: "Max OTs día · SLA · blocked plans",
    icon: MapPinned,
    to: "/admin/zonas",
    color: "amber",
    group: "Gestión Geográfica",
    roles: ["admin", "super_admin", "admin_zone"],
    perm: "M37.zone_rules.view",
  },
  {
    id: "trazabilidad",
    name: "Trazabilidad App / Odoo",
    tagline: "Logs y payloads · código de rastreo",
    icon: Activity,
    to: "/integraciones/trazabilidad",
    color: "purple",
    group: "Integración",
    roles: ["admin", "super_admin"],
  },
  {
    // v101.45.1 · Rellenar VLANs → IPs → relaciones VLAN↔equipo desde Odoo (vista previa + avance)
    id: "infra-red",
    name: "Infraestructura de red",
    tagline: "VLANs · IPs · relaciones · rellenar desde Odoo",
    icon: Database,
    to: "/integraciones/infraestructura-red",
    color: "green",
    group: "Integración",
    roles: ["super_admin"],
  },
  {
    id: "odoo-live",
    name: "Monitoreo Odoo Live",
    tagline: "Live-Sync · poll manual",
    icon: Cable,
    to: "/admin/odoo-live",
    color: "green",
    group: "Integración",
    roles: ["admin", "super_admin"],
  },
  {
    id: "zonas",
    name: "Gestión Geográfica",
    tagline: "Zonas · Bodegas · Reglas · Técnicos",
    icon: MapPin,
    to: "/admin/zonas",
    color: "amber",
    group: "Gestión Geográfica",
    // Cualquiera con permisos de la categoría "Gestión Geográfica" ve la card.
    // La ruta también acepta el permiso atómico M19.zones.view (fallback OR).
    roles: ["admin", "super_admin", "admin_zone"],
    perm: "M19.zones.view",
  },
  {
    id: "kpi-tecnicos",
    name: "KPI Técnicos",
    tagline: "Capacidad por sucursal y zona",
    icon: CalendarClock,
    to: "/kpi-tecnicos",
    color: "cyan",
    group: "Gestión de Operación",
    roles: ["admin", "super_admin", "admin_zone", "supervisor", "agendador"],
  },
  {
    id: "horarios",
    name: "Horarios de Técnicos",
    tagline: "Turnos + descansos",
    icon: CalendarClock,
    to: "/admin/horarios",
    color: "teal",
    group: "Administración de Usuarios",
    perm: "M_SHIFTS.view",
  },
  // v10 · ELIMINADO del launcher: Imprevistos y Alertas (consolidado en A3)
  {
    id: "mapeo-bodegas",
    name: "Bodegas y Mapeo Geográfico",
    tagline: "Bodegas Odoo · asignación a técnicos",
    icon: Warehouse,
    to: "/admin/mapeo-bodegas",
    color: "amber",
    group: "Gestión Geográfica",
    roles: ["admin", "super_admin", "admin_zone"],
    perm: "M8.warehouses.view",
  },
  // v101.179.0 · Métricas · Lead Time (dashboard + reportería + envío programado)
  {
    id: "metricas-lead-time",
    name: "Lead Time",
    tagline: "Tiempos de atención y cumplimiento 6 / 9 / 12 / 24 h",
    icon: Timer,
    to: "/metricas/lead-time",
    color: "teal",
    group: "Métricas",
    roles: ["admin", "super_admin", "admin_zone", "supervisor"],
    submenu: [
      { label: "Dashboard", to: "/metricas/lead-time" },
    ],
  },
  // v101.263.0 · KPI del sistema: panel ejecutivo + paneles de detalle por indicador
  {
    id: "metricas-kpi",
    name: "KPI del sistema",
    tagline: "Panel ejecutivo y detalle de 16 indicadores de la operación",
    icon: Timer,
    to: "/metricas/kpi",
    color: "teal",
    group: "Métricas",
    roles: ["admin", "super_admin", "admin_zone", "supervisor", "finance"],
    submenu: [
      { label: "Panel ejecutivo", to: "/metricas/kpi" },
      { label: "Lead Time", to: "/metricas/kpi/lead_time" },
      { label: "Agendamiento", to: "/metricas/kpi/agendamiento" },
      { label: "Errores", to: "/metricas/kpi/errores" },
    ],
  },
  // v101.196.0 · Conversaciones de WhatsApp: Fibi (IA) atiende y deriva a los agendadores de la sucursal
  {
    id: "conversaciones-whatsapp",
    name: "Conversaciones WhatsApp",
    tagline: "Fibi agenda y atiende · clientes en espera por sucursal · chat con el cliente",
    icon: MessageSquare,
    to: "/whatsapp/conversaciones",
    color: "green",
    group: "Gestión de Operación",
    roles: ["admin", "super_admin", "admin_zone", "supervisor", "agendador", "scheduler", "atc"],
  },
  // v101.193.0 · Prefacturación de contratistas (Control de Calidad · Contabilidad)
  {
    id: "prefacturacion",
    name: "Prefacturación",
    tagline: "Contratistas · actividades, correcciones y cambios de tipo · PDF y Excel",
    icon: Receipt,
    to: "/prefacturacion",
    color: "teal",
    group: "Gestión de Operación",
    roles: ["admin", "super_admin", "fiscalizer", "finance", "admin_zone"],
  },
  // v101.123.0 · Reporte diario de instalaciones para Comercial (por sucursal, por correo)
  {
    id: "reporte-comercial",
    name: "Reporte Comercial",
    tagline: "Instalaciones de venta nueva por sucursal · correo diario",
    icon: Mail,
    to: "/reporte-comercial",
    color: "amber",
    group: "Gestión de Operación",
    roles: ["admin", "super_admin", "admin_zone", "supervisor"],
  },
  // v101.255.0 · Reportes › Fiscalización IA
  {
    id: "reporte-fiscalizacion-ia",
    name: "Reportes · Fiscalización IA",
    tagline: "Resultado de la IA por OT · KPIs, rechazos, historial y exportación",
    icon: ShieldCheck,
    to: "/reportes/fiscalizacion-ia",
    color: "teal",
    group: "Gestión de Operación",
    roles: ["admin", "super_admin", "admin_zone", "supervisor", "fiscalizer", "finance"],
  },
  // v101.102.0 · Centro de Validaciones (aprovisionar / finalizar / cerrar)
  {
    id: "validaciones",
    name: "Centro de Validaciones",
    tagline: "Qué se exige en cada aprovisionamiento · prender / apagar",
    icon: Shield,
    to: "/admin/validaciones",
    color: "purple",
    group: "Gestión de Operación",
    roles: ["admin", "super_admin", "admin_zone", "supervisor"],
  },
  // v101.101.0 · Catálogo de motivos de cierre (app y web)
  {
    id: "motivos-cierre",
    name: "Motivos de Cierre y Cancelación",   // v101.188.0
    tagline: "Listas para cerrar y para cancelar OTs",
    icon: ClipboardList,
    to: "/admin/motivos-cierre",
    color: "teal",
    group: "Gestión de Operación",
    roles: ["admin", "super_admin"],
  },
  // v10 · ELIMINADO del launcher: Mapeo de Stages (configuración interna)
  // v10 · CONSOLIDADO en "Gestión WhatsApp": Negociaciones, Plantillas, Notificaciones
  {
    id: "a2-bitacora",
    name: "Bitácora A2",
    tagline: "Auto-agendamiento en vivo",
    icon: Activity,
    to: "/admin/a2-bitacora",
    color: "green",
    group: "Centro de Inteligencia",
    perm: "M32.a2.view",
    roles: ["admin", "super_admin", "admin_zone", "supervisor"],
  },
  {
    id: "provisioning-audit",
    name: "Auditoría Aprovisionamiento",
    tagline: "Trazabilidad OLT · discover · set-ont",
    icon: Activity,
    to: "/admin/provisioning-audit",
    color: "red",
    group: "Integración",
    roles: ["admin", "super_admin", "admin_zone", "supervisor"],
  },
  // v3.1.1 · DEPRECADO · La bandeja "Rescate Manual A2" fue reemplazada por el
  // carril REVISIÓN en el Smart Kanban (Buzón de Excepciones universal A1/A2/A6).
  // Se oculta del módulo pero la ruta sigue activa por si hay bookmarks legacy.
  // {
  //   id: "a2-rescate",
  //   name: "Rescate Manual A2",
  //   ...
  // },
  // v10 · CONSOLIDADO en "Gestión WhatsApp"

  // Sub-módulos ocultos del launcher (se acceden desde el hub reglas-agentes)
  // v10 · CONSOLIDADO en Agente A3 · Eventos y Calamidades (submenu)
  {
    id: "cola-a6",
    name: "Cola de Despacho",
    tagline: "A6 · Priorización",
    icon: Target,
    to: "/admin/cola-a6",
    color: "purple",
    group: "Centro de Inteligencia",
    hidden: true,
    roles: ["admin", "super_admin", "admin_zone", "supervisor"],
  },
  {
    id: "rutas-a4",
    name: "Rutas del Día",
    tagline: "A4 · TSP + GPS",
    icon: Route,
    to: "/admin/rutas-a4",
    color: "blue",
    group: "Centro de Inteligencia",
    hidden: true,
    roles: ["admin", "super_admin", "admin_zone", "supervisor"],
  },
  {
    id: "usuarios",
    name: "Usuarios",
    tagline: "Gestión de personas",
    icon: Users,
    to: "/admin/usuarios",
    color: "indigo",
    group: "Administración de Usuarios",
    perm: "M17.users.view",
  },
  {
    id: "roles",
    name: "Roles y Permisos",
    tagline: "Control de acceso",
    icon: Shield,
    to: "/admin/roles",
    color: "red",
    group: "Administración de Usuarios",
    perm: "M17.roles.view",
  },
  {
    id: "guia-roles",
    name: "Guía de Roles",
    tagline: "Manual · 17 módulos",
    icon: BookOpen,
    to: "/admin/guia-roles",
    color: "amber",
    group: "Administración de Usuarios",
    // Fallback de roles: super_admin/admin/admin_zone SIEMPRE ven la Guía
    // aunque el permiso M17.roles.view no esté hidratado en el token.
    // Si el user tiene M17.roles.view por permiso atómico, también entra.
    roles: ["super_admin", "admin", "admin_zone"],
    perm: "M17.roles.view",
    // Oculta del launcher: se accede desde el Centro de Documentación AlfaNet.
    // La ruta sigue navegable por URL directa para compatibilidad.
    hidden: true,
  },
  {
    id: "auditoria",
    name: "Auditoría",
    tagline: "Trazabilidad y logs",
    icon: History,
    to: "/admin/auditoria",
    color: "purple",
    group: "Administración de Usuarios",
    perm: "M17.audit.view",
  },
  {
    id: "notificaciones",
    name: "Notificaciones",
    tagline: "Correos Mailgun",
    icon: Mail,
    to: "/admin/notificaciones",
    color: "pink",
    group: "Administración de Usuarios",
    perm: "M17.notifications.view",
  },
  {
    id: "explorador-db",
    name: "Explorador DB",
    tagline: "PostgreSQL live",
    icon: Database,
    to: "/explorador-db",
    color: "slate",
    group: "Integración",
    roles: ["admin", "super_admin", "finance"],
  },
  {
    // v101.147.0 · Ambiente de pruebas RISE: integraciones y sincronización manual
    id: "ambiente-rise",
    name: "Ambiente RISE (pruebas)",
    tagline: "Integraciones · sincronizar a mano",
    icon: Activity,
    to: "/integraciones/ambiente-rise",
    color: "amber",
    group: "Integración",
    roles: ["admin", "super_admin"],
  },
  {
    // v101.162.0 · Sincronización con Odoo + trazabilidad de cada OT
    id: "sincronizacion-odoo",
    name: "Sincronización con Odoo",
    tagline: "OTs desincronizadas y trazabilidad",
    icon: Activity,
    to: "/integraciones/sincronizacion-odoo",
    color: "slate",
    group: "Integración",
    roles: ["admin", "super_admin", "admin_zone", "supervisor", "agendador", "scheduler"],
  },
  {
    // v101.144.0 · Agendamiento automático y confirmación por WhatsApp, por sucursal
    id: "agendamiento-sucursales",
    name: "Agendamiento, WhatsApp e IA por sucursal",
    tagline: "Prender / apagar por sucursal",
    icon: Activity,
    to: "/integraciones/agendamiento-sucursales",
    color: "slate",
    group: "Integración",
    roles: ["admin", "super_admin", "admin_zone", "supervisor", "agendador", "scheduler"],
  },
  {
    id: "system-triggers",
    name: "Gestor de Triggers",
    tagline: "Procesos en segundo plano · intervalos",
    icon: Activity,
    to: "/integraciones/triggers",
    color: "slate",
    group: "Integración",
    perm: "M40.triggers.view",
  },
  {
    id: "db-stats",
    name: "Tamaño de la BD",
    tagline: "Top-20 tablas · diagnóstico",
    icon: HardDrive,
    to: "/admin/db-stats",
    color: "amber",
    group: "Integración",
    roles: ["admin", "super_admin"],
  },
  {
    id: "a1-audit",
    name: "Agente A1 · Auditoría",
    tagline: "OTs pendientes por motivo · re-evaluar",
    icon: Brain,
    to: "/admin/a1-audit",
    color: "cyan",
    group: "Centro de Inteligencia",
    hidden: true,
    roles: ["admin", "super_admin", "planner", "pmo"],
  },
  {
    id: "a1-settings",
    name: "Agente A1 · Configuración",
    tagline: "Reglas editables sin redeploy",
    icon: SlidersHorizontal,
    to: "/admin/a1-settings",
    color: "purple",
    group: "Centro de Inteligencia",
    hidden: true,
    roles: ["admin", "super_admin"],
  },

  // ══════════════ DOCUMENTACIÓN ══════════════
  // Centro unificado · reemplaza los accesos directos individuales.
  // Los módulos individuales (flujos, flujo-sistema, documentacion, manual,
  // manual-inteligencia) se mantienen navegables por ruta pero ocultos del
  // launcher para dejar sólo la tarjeta principal.
  {
    id: "centro-documentacion",
    name: "Centro de Documentación AlfaNet",
    tagline: "Manuales · Guías · Tutoriales",
    icon: BookOpen,
    to: "/centro-documentacion",
    color: "teal",
    group: "Documentación y Sistema",
    // Sin restricciones: disponible para todos los usuarios autenticados.
  },
  {
    id: "flujo-sistema",
    name: "Flujo del Sistema",
    tagline: "Diagrama global",
    icon: Map,
    to: "/flujo-sistema",
    color: "cyan",
    group: "Documentación y Sistema",
    perm: "M20.view",
    hidden: true,
  },
  {
    id: "flujos",
    name: "Flujos de Decisión",
    tagline: "Árboles de negocio",
    icon: Workflow,
    to: "/flujos",
    color: "fuchsia",
    group: "Documentación y Sistema",
    perm: "M20.view",
    hidden: true,
  },
  {
    id: "documentacion",
    name: "Documentación",
    tagline: "Especificaciones",
    icon: FileText,
    to: "/documentacion",
    color: "lime",
    group: "Documentación y Sistema",
    perm: "M20.view",
    hidden: true,
  },
  {
    id: "manual",
    name: "Manual de Usuario",
    tagline: "Guía de uso",
    icon: BookOpen,
    to: "/manual",
    color: "rose",
    group: "Documentación y Sistema",
    perm: "M20.view",
    hidden: true,
  },
  {
    // v101.24.0 · Fibi · Ayuda IA: asistente virtual sobre cómo funciona el sistema (todos los usuarios)
    id: "ayuda-ia",
    name: "Fibi · Ayuda IA",
    tagline: "Tu asistente virtual · pregúntame",
    icon: Bot,
    to: "/ayuda",
    color: "teal",
    group: "Documentación y Sistema",
  },
  {
    // ★ Manual de Inteligencia AlfaNet · documentación cliente-ready de A1-A6
    id: "manual-inteligencia",
    name: "Manual de Inteligencia AlfaNet",
    tagline: "Cerebro de agentes explicado",
    icon: Brain,
    to: "/manual-inteligencia",
    color: "purple",
    group: "Documentación y Sistema",
    hidden: true,
  },
];

/**
 * Devuelve el módulo activo según la ruta actual.
 * Coincide por prefijo más largo.
 */
export function findActiveModule(pathname) {
  if (!pathname || pathname === "/" || pathname === "/apps") return null;
  let best = null;
  for (const m of APP_MODULES) {
    if (pathname === m.to || pathname.startsWith(m.to + "/")) {
      if (!best || m.to.length > best.to.length) best = m;
    }
  }
  return best;
}

/**
 * Filtra módulos visibles según el usuario.
 * @param {boolean} includeHidden - Si es true, incluye módulos marcados con `hidden: true`.
 *                                  Úsalo para permitir navegación pero ocultar del launcher.
 */
export function filterVisibleModules(modules, user, hasPerm, includeHidden = false) {
  if (!user) return [];
  // v3.6.3 · Bypass total: super_admin ve TODOS los módulos (excepto los explícitamente hidden)
  const isSuperAdmin = user.role === "super_admin";
  return modules.filter((m) => {
    if (m.hidden && !includeHidden) return false;
    if (isSuperAdmin) return true;
    // Semántica OR: si tiene ambos (roles Y perm), basta con que uno se cumpla.
    const hasRoleMatch = m.roles ? m.roles.includes(user.role) : null;
    const hasPermMatch = m.perm ? hasPerm(m.perm) : null;
    if (hasRoleMatch === null && hasPermMatch === null) return true; // sin restricciones
    if (hasRoleMatch === true || hasPermMatch === true) return true;
    return false;
  });
}
